(function () {
  "use strict";

  // ─── CONFIG ────────────────────────────────────────────────────────────────
  var APP_VERSION       = "retail_v1";
  var EXPERIENCE        = "retail_qr";
  var CLARITY_PROJECT   = "izsv8i9q9z";
  var SUMMARY_INTERVAL  = 30000;   // ms between periodic session summaries
  var CLARITY_RETRY_MS  = 2000;    // ms to wait before retrying Clarity tag
  var SCROLL_MILESTONES = [25, 50, 75, 100];

  // ─── STATE ─────────────────────────────────────────────────────────────────
  var s = {
    // Identity
    scan_id:          null,
    device_id:        null,
    scan_start_ts:    null,
    scan_start_ms:    Date.now(),
    is_new_scan_24h:  false,

    // Screens
    current_screen:   null,
    screen_enter_ms:  null,
    screens_reached:  [],

    // Demo modes
    current_mode_id:    null,
    current_mode_name:  null,
    mode_enter_ms:      null,
    mode_sequence:      0,
    modes_tried:        [],
    mode_had_play:      false,
    mode_play_count:    0,

    // Tracks
    tracks_played:        {},   // track_id -> count in this scan
    current_track_id:     null,
    track_play_start_ms:  null,
    plays_in_scan:        0,
    first_track_id:       null,
    first_mode_id:        null,

    // Aggregates
    player_reached:   false,
    song_played:      false,
    max_scroll:       0,
    scroll_fired:     {},       // screen -> { 25: true, ... }

    // Internals
    summary_timer:    null,
    clarity_ready:    false
  };

  // ─── IDENTITY ──────────────────────────────────────────────────────────────

  function pad(n) { return String(n).padStart ? String(n).padStart(2, "0") : (n < 10 ? "0" + n : String(n)); }

  function makeId(prefix) {
    var d = new Date();
    var date = d.getFullYear() + "" + pad(d.getMonth() + 1) + "" + pad(d.getDate());
    var time = pad(d.getHours()) + "" + pad(d.getMinutes()) + "" + pad(d.getSeconds());
    var rand = Math.random().toString(36).substr(2, 6);
    return prefix + "_" + date + "_" + time + "_" + rand;
  }

  function makeDeviceId() {
    return "dev_" + Math.random().toString(36).substr(2, 8);
  }

  function initIdentity() {
    try {
      // scan_id — fresh every browser session (tab open = one scan)
      var existing = sessionStorage.getItem("retail_scan_id");
      if (!existing) {
        s.scan_id = makeId("scan");
        sessionStorage.setItem("retail_scan_id", s.scan_id);
      } else {
        s.scan_id = existing;
      }

      // device_id — persists across sessions on same browser
      var dev = localStorage.getItem("retail_device_id");
      if (!dev) {
        s.device_id = makeDeviceId();
        localStorage.setItem("retail_device_id", s.device_id);
      } else {
        s.device_id = dev;
      }

      // 24h unique scan check
      var lastTs = localStorage.getItem("retail_last_scan_ts");
      var now    = Date.now();
      s.is_new_scan_24h = !lastTs || (now - parseInt(lastTs, 10)) > 86400000;
      localStorage.setItem("retail_last_scan_ts", String(now));

      // scan_start_ts — ISO string, stable for this session
      var startTs = sessionStorage.getItem("retail_scan_start_ts");
      if (!startTs) {
        s.scan_start_ts = new Date().toISOString();
        sessionStorage.setItem("retail_scan_start_ts", s.scan_start_ts);
      } else {
        s.scan_start_ts = startTs;
      }
    } catch (e) {
      // localStorage/sessionStorage blocked (private mode, etc.)
      s.scan_id      = makeId("scan");
      s.device_id    = makeDeviceId();
      s.scan_start_ts = new Date().toISOString();
      s.is_new_scan_24h = true;
    }
  }

  // ─── GA4 HELPERS ───────────────────────────────────────────────────────────

  function sendGA4(name, extra) {
    if (typeof window.gtag !== "function") return;
    try {
      var params = {
        experience:        EXPERIENCE,
        scan_id:           s.scan_id,
        device_id:         s.device_id,
        is_new_scan_24h:   String(s.is_new_scan_24h),
        scan_start_ts:     s.scan_start_ts,
        session_elapsed_ms: Date.now() - s.scan_start_ms,
        screen_name:       s.current_screen || "unknown",
        app_version:       APP_VERSION
      };
      if (extra) {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) params[k] = extra[k];
        }
      }
      window.gtag("event", name, params);
    } catch (e) {}
  }

  // ─── CLARITY HELPERS ───────────────────────────────────────────────────────

  function claritySet(key, value) {
    if (typeof window.clarity !== "function") return;
    if (value === undefined || value === null) return;
    try { window.clarity("set", key, String(value)); } catch (e) {}
  }

  function tagClaritySession() {
    if (typeof window.clarity !== "function") return;
    claritySet("experience",       EXPERIENCE);
    claritySet("scan_id",          s.scan_id);
    claritySet("device_id",        s.device_id);
    claritySet("is_new_scan_24h",  String(s.is_new_scan_24h));
    claritySet("scan_start_ts",    s.scan_start_ts);
    claritySet("app_version",      APP_VERSION);
    s.clarity_ready = true;
  }

  function loadClarityFallback() {
    // Only loads if Clarity never arrived via content_for_header
    if (typeof window.clarity === "function") return;
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      if (y && y.parentNode) y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_PROJECT);
  }

  function initClarity() {
    if (typeof window.clarity === "function") {
      tagClaritySession();
    } else {
      // Clarity loads async — retry, then fall back to manual load
      window.setTimeout(function () {
        if (typeof window.clarity === "function") {
          tagClaritySession();
        } else {
          loadClarityFallback();
          window.setTimeout(tagClaritySession, CLARITY_RETRY_MS);
        }
      }, CLARITY_RETRY_MS);
    }
  }

  // ─── VIRTUAL URL (for per-screen Clarity heatmaps) ─────────────────────────
  // Pushes ?screen=xxx into the URL so Clarity creates a separate heatmap
  // for each screen. Uses replaceState (no browser history entry added).

  function setVirtualScreenUrl(screenName) {
    try {
      if (typeof history !== "undefined" && history.replaceState) {
        history.replaceState(null, "", window.location.pathname + "?screen=" + screenName);
      }
    } catch (e) {}
  }

  // ─── SCREEN TRACKING ───────────────────────────────────────────────────────

  function onScreenView(event) {
    var d           = (event && event.detail) || {};
    var screenName  = d.screen_name || "unknown";
    var prevScreen  = d.previous_screen_name || s.current_screen || "";
    var now         = Date.now();

    // Fire screen_exit for whatever screen we're leaving
    if (s.current_screen && s.screen_enter_ms) {
      sendGA4("retail_screen_exit", {
        screen_name:      s.current_screen,
        screen_time_ms:   now - s.screen_enter_ms,
        next_screen_name: screenName,
        exit_reason:      "next_screen"
      });
    }

    // Update state
    s.current_screen  = screenName;
    s.screen_enter_ms = now;
    if (s.screens_reached.indexOf(screenName) === -1) {
      s.screens_reached.push(screenName);
    }

    // Send screen_view
    sendGA4("retail_screen_view", {
      screen_name:          screenName,
      previous_screen_name: prevScreen
    });

    // Virtual URL so Clarity builds a separate heatmap per screen
    setVirtualScreenUrl(screenName);

    // Clarity tags
    claritySet("current_screen_name", screenName);
    claritySet("screens_reached",     s.screens_reached.join(","));

    if (screenName === "player") {
      s.player_reached = true;
      claritySet("player_reached", "true");
    }

    // Reset scroll milestones for this screen
    if (!s.scroll_fired[screenName]) {
      s.scroll_fired[screenName] = {};
    }
  }

  // ─── CONNECT CTA ───────────────────────────────────────────────────────────

  function onConnectCta(event) {
    var d = (event && event.detail) || {};
    sendGA4("retail_connect_cta_click", {
      cta_id:     d.cta_id || "connect_continue",
      screen_name: "connect"
    });
  }

  // ─── DEMO MODE ─────────────────────────────────────────────────────────────

  function onDemoModeSelect(event) {
    var d      = (event && event.detail) || {};
    var modeId = d.demo_mode_id   || "";
    var modeName = d.demo_mode_name || "";
    var prevId = s.current_mode_id;
    var now    = Date.now();

    // Close out previous mode — fire dwell + abandon if no play
    if (prevId && s.mode_enter_ms) {
      var dwellMs = now - s.mode_enter_ms;
      sendGA4("retail_demo_mode_dwell", {
        demo_mode_id:         prevId,
        demo_mode_name:       s.current_mode_name || "",
        demo_mode_dwell_ms:   dwellMs,
        mode_had_song_play:   s.mode_had_play,
        mode_track_play_count: s.mode_play_count,
        mode_exit_reason:     "mode_switch"
      });
      if (!s.mode_had_play) {
        sendGA4("retail_demo_mode_abandon", {
          demo_mode_id:       prevId,
          demo_mode_dwell_ms: dwellMs,
          abandon_reason:     "mode_switch"
        });
      }
    }

    // Sequence & first-mode flag
    s.mode_sequence += 1;
    var isFirst = s.mode_sequence === 1;

    sendGA4("retail_demo_mode_select", {
      demo_mode_id:                  modeId,
      demo_mode_name:                modeName,
      previous_demo_mode_id:         prevId || "",
      demo_mode_sequence_number:     s.mode_sequence,
      is_first_demo_mode:            isFirst,
      previous_mode_had_song_play:   s.mode_had_play,
      previous_mode_play_duration_ms: (prevId && s.mode_enter_ms) ? now - s.mode_enter_ms : 0
    });

    // Update state
    s.current_mode_id   = modeId;
    s.current_mode_name = modeName;
    s.mode_enter_ms     = now;
    s.mode_had_play     = false;
    s.mode_play_count   = 0;

    if (s.modes_tried.indexOf(modeId) === -1) s.modes_tried.push(modeId);

    if (!s.first_mode_id) {
      s.first_mode_id = modeId;
      claritySet("first_demo_mode_id", modeId);
    }
    claritySet("last_demo_mode_id",    modeId);
    claritySet("demo_modes_tried_count", String(s.modes_tried.length));
  }

  // ─── TRACK EVENTS ──────────────────────────────────────────────────────────

  function onTrackPlay(event) {
    var d       = (event && event.detail) || {};
    var trackId = d.track_id   || "";
    var now     = Date.now();

    if (!s.tracks_played[trackId]) s.tracks_played[trackId] = 0;
    s.tracks_played[trackId]++;
    s.plays_in_scan++;

    var isReplay = s.tracks_played[trackId] > 1;
    var isFirst  = s.plays_in_scan === 1;

    // Mode-level
    s.mode_had_play  = true;
    s.mode_play_count++;

    // Session-level
    s.current_track_id    = trackId;
    s.track_play_start_ms = now;

    if (!s.first_track_id) {
      s.first_track_id = trackId;
      claritySet("first_track_id", trackId);
    }
    s.song_played = true;
    claritySet("song_played",       "true");
    claritySet("tracks_played_count", String(s.plays_in_scan));

    // Replay event
    if (isReplay) {
      sendGA4("retail_track_replay", {
        track_id:            trackId,
        demo_mode_id:        d.demo_mode_id || s.current_mode_id || "",
        replay_count_in_scan: s.tracks_played[trackId]
      });
    }

    sendGA4("retail_track_play", {
      track_id:             trackId,
      track_title:          d.track_title     || "",
      demo_mode_id:         d.demo_mode_id    || s.current_mode_id  || "",
      demo_mode_name:       d.demo_mode_name  || s.current_mode_name || "",
      play_count_in_scan:   s.plays_in_scan,
      is_first_play_in_scan: isFirst,
      is_replay:            isReplay,
      time_to_first_play_ms: isFirst ? now - s.scan_start_ms : 0
    });
  }

  function onTrackPause(event) {
    var d   = (event && event.detail) || {};
    var now = Date.now();
    sendGA4("retail_track_pause", {
      track_id:         d.track_id       || s.current_track_id || "",
      demo_mode_id:     d.demo_mode_id   || s.current_mode_id  || "",
      position_seconds: d.position_seconds || 0,
      position_percent: d.position_percent || 0,
      play_duration_ms: s.track_play_start_ms ? now - s.track_play_start_ms : 0
    });
    s.track_play_start_ms = null;
  }

  function onTrackComplete(event) {
    var d   = (event && event.detail) || {};
    var now = Date.now();
    sendGA4("retail_track_complete", {
      track_id:              d.track_id      || s.current_track_id || "",
      demo_mode_id:          d.demo_mode_id  || s.current_mode_id  || "",
      track_duration_seconds: d.track_duration_seconds || 0,
      play_duration_ms:       s.track_play_start_ms ? now - s.track_play_start_ms : 0
    });
    s.track_play_start_ms = null;
  }

  function onTrackSkip(event) {
    var d   = (event && event.detail) || {};
    var now = Date.now();
    sendGA4("retail_track_skip", {
      track_id:           d.track_id          || s.current_track_id || "",
      demo_mode_id:       d.demo_mode_id      || s.current_mode_id  || "",
      skipped_at_percent: d.skipped_at_percent || 0,
      play_duration_ms:   s.track_play_start_ms ? now - s.track_play_start_ms : 0,
      skip_reason:        d.skip_reason        || "track_change"
    });
    s.track_play_start_ms = null;
  }

  function onRetailError(event) {
    var d = (event && event.detail) || {};
    sendGA4("retail_error", {
      error_type:          d.error_type          || "js_error",
      error_message_short: d.error_message_short || "",
      demo_mode_id:        d.demo_mode_id        || s.current_mode_id  || "",
      track_id:            d.track_id            || s.current_track_id || ""
    });
  }

  // ─── SCROLL DEPTH ──────────────────────────────────────────────────────────

  function checkScrollDepth() {
    var scrollTop = window.pageYOffset
      || (document.documentElement && document.documentElement.scrollTop)
      || 0;
    var docH = Math.max(
      (document.documentElement && document.documentElement.scrollHeight) || 0,
      (document.body && document.body.scrollHeight) || 0
    ) - window.innerHeight;

    if (docH <= 0) return;

    var pct   = Math.min(100, Math.round((scrollTop / docH) * 100));
    var screen = s.current_screen || "unknown";

    if (!s.scroll_fired[screen]) s.scroll_fired[screen] = {};

    SCROLL_MILESTONES.forEach(function (m) {
      if (pct >= m && !s.scroll_fired[screen][m]) {
        s.scroll_fired[screen][m] = true;
        if (m > s.max_scroll) s.max_scroll = m;
        sendGA4("retail_scroll_depth", {
          screen_name:   screen,
          depth_percent: m
        });
      }
    });
  }

  function setupScrollTracking() {
    var ticking = false;
    window.addEventListener("scroll", function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          checkScrollDepth();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ─── SESSION SUMMARY & EXIT ────────────────────────────────────────────────

  function summaryParams() {
    return {
      session_duration_ms:    Date.now() - s.scan_start_ms,
      current_screen_name:    s.current_screen || "unknown",
      max_scroll_depth:       s.max_scroll,
      screens_reached:        s.screens_reached.join(","),
      demo_modes_tried_count: s.modes_tried.length,
      tracks_played_count:    s.plays_in_scan,
      song_played:            String(s.song_played),
      player_reached:         String(s.player_reached),
      last_demo_mode_id:      s.current_mode_id   || "",
      last_track_id:          s.current_track_id  || ""
    };
  }

  function sendSummary() {
    sendGA4("retail_session_summary", summaryParams());
    claritySet("last_screen_name",       s.current_screen || "");
    claritySet("demo_modes_tried_count", String(s.modes_tried.length));
    claritySet("tracks_played_count",    String(s.plays_in_scan));
    claritySet("max_scroll_depth",       String(s.max_scroll));
  }

  function setupSessionSummary() {
    s.summary_timer = setInterval(sendSummary, SUMMARY_INTERVAL);
  }

  function setupExitTracking() {
    window.addEventListener("pagehide", function () {
      if (s.summary_timer) clearInterval(s.summary_timer);

      var now = Date.now();

      // Close out current demo mode
      if (s.current_mode_id && s.mode_enter_ms) {
        var dwellMs = now - s.mode_enter_ms;
        sendGA4("retail_demo_mode_dwell", {
          demo_mode_id:          s.current_mode_id,
          demo_mode_name:        s.current_mode_name || "",
          demo_mode_dwell_ms:    dwellMs,
          mode_had_song_play:    s.mode_had_play,
          mode_track_play_count: s.mode_play_count,
          mode_exit_reason:      "session_end"
        });
        if (!s.mode_had_play) {
          sendGA4("retail_demo_mode_abandon", {
            demo_mode_id:       s.current_mode_id,
            demo_mode_dwell_ms: dwellMs,
            abandon_reason:     "page_exit"
          });
        }
      }

      // Close out current screen
      if (s.current_screen && s.screen_enter_ms) {
        sendGA4("retail_screen_exit", {
          screen_name:    s.current_screen,
          screen_time_ms: now - s.screen_enter_ms,
          exit_reason:    "pagehide"
        });
      }

      // Final session_end
      var sp = summaryParams();
      sp.exit_screen = s.current_screen || "unknown";
      sp.exit_reason = "pagehide";
      sendGA4("retail_session_end", sp);
    });

    // Periodic backup when tab goes hidden (mobile home button etc.)
    document.addEventListener("visibilitychange", function () {
      if (document.hidden) sendSummary();
    });
  }

  // ─── BIND APP EVENTS ───────────────────────────────────────────────────────

  function bindListeners() {
    window.addEventListener("retail:screen_view",      onScreenView);
    window.addEventListener("retail:connect_cta_click", onConnectCta);
    window.addEventListener("retail:demo_mode_select", onDemoModeSelect);
    window.addEventListener("retail:track_play",       onTrackPlay);
    window.addEventListener("retail:track_pause",      onTrackPause);
    window.addEventListener("retail:track_complete",   onTrackComplete);
    window.addEventListener("retail:track_skip",       onTrackSkip);
    window.addEventListener("retail:error",            onRetailError);
  }

  // ─── INIT ──────────────────────────────────────────────────────────────────

  function init() {
    try {
      initIdentity();
      initClarity();

      sendGA4("retail_scan_start", {
        landing_url:      window.location.href,
        referrer:         document.referrer || "",
        device_type:      window.innerWidth <= 820 ? "mobile" : "desktop",
        screen_width:     window.screen ? window.screen.width  : window.innerWidth,
        screen_height:    window.screen ? window.screen.height : window.innerHeight,
        is_new_scan_24h:  String(s.is_new_scan_24h)
      });

      bindListeners();
      setupScrollTracking();
      setupSessionSummary();
      setupExitTracking();
    } catch (err) {
      // Analytics must never break the main app — fail silently
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

})();
